import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "./Logo";

const CATS = [
  ["indian-spices", "Indian Spices"], ["herbal-powders-extracts", "Herbal Powders & Extracts"],
  ["basmati-rice", "Basmati Rice"], ["non-basmati-rice", "Non-Basmati Rice"],
  ["fresh-produce", "Fresh Produce"], ["millets", "Millets"], ["pulses", "Pulses"],
  ["cereals", "Cereals"], ["oil-seeds", "Oil Seeds"], ["dehydrated-products", "Dehydrated Products"],
  ["whole-herbs", "Whole Herbs"],
];

const LINKS = [
  ["Home", "/"], ["Products", "/products"], ["About", "/#about"],
  ["Gallery", "/gallery"], ["Certifications", "/certifications"], ["Contact", "/contact"],
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      nav("/");
      setTimeout(() => document.querySelector(href.slice(1))?.scrollIntoView({ behavior: "smooth" }), 300);
    } else nav(href);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 border-b ${
        scrolled ? "bg-beige/90 backdrop-blur-xl border-forest/10 py-3" : "bg-transparent border-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" data-testid="logo" className="flex items-center gap-2 group" onClick={() => window.scrollTo(0, 0)}>
          <Logo variant={scrolled ? "dark" : "white"} animate tagline={false} className="transition-colors" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map(([label, href]) =>
            label === "Products" ? (
              <div key={label} onMouseEnter={() => setMega(true)} onMouseLeave={() => setMega(false)} className="relative">
                <button
                  data-testid="nav-products"
                  onClick={() => go(href)}
                  className={`link-sweep text-sm font-medium ${scrolled ? "text-forest" : "text-beige"}`}
                >
                  {label}
                </button>
                <AnimatePresence>
                  {mega && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
                      transition={{ duration: 0.25 }}
                      className="absolute top-8 -left-6 w-[520px] bg-beige border border-forest/10 shadow-[0_20px_60px_rgba(27,59,54,0.15)] p-6 grid grid-cols-2 gap-1"
                    >
                      {CATS.map(([slug, name]) => (
                        <button
                          key={slug}
                          data-testid={`mega-${slug}`}
                          onClick={() => go(`/products?category=${slug}`)}
                          className="text-left text-forest/80 hover:text-forest hover:bg-forest/5 px-3 py-2.5 text-sm font-medium flex items-center justify-between group transition-colors"
                        >
                          {name}
                          <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 text-gold transition-opacity" />
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <button
                key={label}
                data-testid={`nav-${label.toLowerCase().replace(" ", "-")}`}
                onClick={() => go(href)}
                className={`link-sweep text-sm font-medium ${scrolled ? "text-forest" : "text-beige"}`}
              >
                {label}
              </button>
            )
          )}
          <button
            data-testid="nav-quote-btn"
            onClick={() => go("/contact")}
            className={`text-sm font-medium px-5 py-2.5 border transition-all duration-300 ${
              scrolled ? "border-forest text-forest hover:bg-forest hover:text-beige" : "border-beige text-beige hover:bg-gold hover:border-gold hover:text-forest"
            }`}
          >
            Request Quote
          </button>
        </nav>

        <button data-testid="mobile-menu-btn" className="lg:hidden" onClick={() => setOpen(true)}>
          <Menu className={scrolled ? "text-forest" : "text-beige"} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 bg-forest z-50 p-8 flex flex-col grain"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center mb-12">
              <Logo variant="white" tagline={false} />
              <button data-testid="mobile-close-btn" onClick={() => setOpen(false)}><X className="text-beige" /></button>
            </div>
            <div className="flex flex-col gap-2">
              {LINKS.map(([label, href], i) => (
                <button key={label} onClick={() => go(href)} data-testid={`mobile-nav-${label.toLowerCase().replace(" ", "-")}`}
                  className="text-beige font-serif text-4xl text-left py-2 border-b border-beige/10">
                  {label}
                </button>
              ))}
              <button onClick={() => go("/contact")} className="mt-6 bg-gold text-forest py-4 font-medium text-sm uppercase tracking-widest">
                Request Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
